import { components } from 'react-select'

import Icon from 'components/Icon'
import Text from 'components/Text'
import Spinner from 'components/Spinner'

import { StyledOption } from './styles'
import { DropdownItem, IndicatorContainerProps, OptionProps } from './types'
import Box from 'components/Box'
import { formatDatetime } from 'utils/datetime'
import Flex from 'components/Flex'
import LeadStatusChip from 'modules/Leads/shared/LeadStatus/LeadStatus'
import { useTranslation } from 'react-i18next'
import { StyledLinkFlex } from 'modules/shared/styles'
import { useReusableTab } from 'hooks/use-reusable-lead-tab'

export const IndicatorsContainer = <T extends string | number>(
  props: IndicatorContainerProps<DropdownItem<T>>,
  showIcon: boolean,
) => (
  <components.IndicatorsContainer {...props}>
    <components.DropdownIndicator {...props} isFocused={false} innerProps={{}}>
      {props.selectProps.isLoading ? (
        <Spinner fontSize={16} />
      ) : showIcon ? (
        <Icon
          color={props.selectProps.isDisabled ? 'gray5' : props.selectProps.iconColor || 'blue1'}
          icon={props.selectProps.icon || ['far', props.selectProps?.menuIsOpen ? 'angle-up' : 'angle-down']}
        />
      ) : (
        <></>
      )}
    </components.DropdownIndicator>
  </components.IndicatorsContainer>
)

export const Option = <T extends string | number>(props: OptionProps<DropdownItem<T>>) => (
  <StyledOption {...props} indention={props.data?.indention}>
    <Box alignItems="center" display="flex" gap="8px">
      {props.data.icon && <Icon color={props.data?.iconColor ?? 'blue1'} icon={props.data.icon} />}
      <Box display="flex" flexDirection="column">
        <Text
          color={props.isSelected ? 'blue1' : props.data?.color || 'defaultText'}
          inline
          isCapitalizedFirstLetter
          lineHeight={props.data.subText ? '22px' : undefined}
        >
          {props.data.label}
        </Text>
        {props.data.subText && (
          <Text color={props.data.subTextColor || 'gray5'} fontSize={12} lineHeight="14px">
            {props.data.subText}
          </Text>
        )}
      </Box>
    </Box>
  </StyledOption>
)

export const OptionLeadMerge = <T extends string | number>(props: OptionProps<DropdownItem<T>>) => {
  const { data, isSelected } = props
  const { t } = useTranslation()
  const { openInReusableTab } = useReusableTab()

  return (
    <StyledOption {...props} isFixedWoIcon={false} indention={data?.indention} width="100%">
      <Box alignItems="center" display="flex" justifyContent="space-between" width="100%">
        <Box display="flex" flexDirection="column">
          <Text
            color={isSelected ? 'blue1' : data?.color || 'defaultText'}
            fontSize="14px"
            inline
            isCapitalizedFirstLetter
            lineHeight={data.subText ? '22px' : undefined}
          >
            <StyledLinkFlex
              to={`/lead/${data.value}`}
              onClick={(e) => {
                e.preventDefault()
                openInReusableTab(`/lead/${data.value}`)
              }}
            >
              {data.label}
              {data.icon && <Icon marginLeft="4px" color="blue1" icon={data.icon} />}
            </StyledLinkFlex>
          </Text>
          <Flex alignItems="center" justifyContent="flex-start" marginTop="4px" gap="4px" padding="0">
            {data.data?.lead_status_group_name && (
              <LeadStatusChip statusGroup={data.data.lead_status_group_name.toLowerCase()} />
            )}
            {(data.data?.lead_owner_name || data.data?.lead_owner_team_name) && (
              <Text display="inline" color="gray6" lineHeight="16px" marginRight="6px" fontSize={12}>
                {data?.data?.lead_owner_name || data.data?.lead_owner_team_name}
              </Text>
            )}
            {data.data?.last_activity?.last_activity_ts && (
              <>
                <Box borderRadius="50%" backgroundColor="gray4" minHeight="4px" minWidth="4px" marginRight="6px" />
                <Text display="inline" color="gray6" fontSize={12} lineHeight="16px">
                  {t('last_activity_lbl')} {formatDatetime(data.data.last_activity.last_activity_ts)}
                </Text>
              </>
            )}
          </Flex>
        </Box>
        {isSelected && <Icon icon={['fas', 'check']} color={'blue1'} fontSize={16} />}
      </Box>
    </StyledOption>
  )
}
